import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Chip, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledTypography } from '../../../components/styles/styles';
import { NewsItemAdmin } from '../../../models/NewsItem';

type Props = {
  items: NewsItemAdmin[];
  loading: boolean;
  showArchived: boolean;
  onToggleArchived: (value: boolean) => void;
  onEdit: (item: NewsItemAdmin) => void;
  onDelete: (item: NewsItemAdmin) => void;
  onUnarchive: (item: NewsItemAdmin) => void;
  onPublish: (item: NewsItemAdmin) => void;
};

// Each status tone is self-contained — background/text/border all drawn from
// the one Palette entry — matching how StyledModal's alert bar and the
// cashflow-report delete icon pick a single semantic tone rather than mixing
// colors across tones.
const STATUS_STYLE: Record<string, { label: string; tone: keyof typeof Palette }> = {
  draft: { label: 'Draft', tone: 'tone2' },
  visible: { label: 'Live', tone: 'active' },
  archived: { label: 'Archived', tone: 'warning' },
};

// 'wordpress'/'generic' items fall through to the Website style — they're
// all link-out posts from the admin's point of view. 'manual' stays a plain
// outlined neutral tag; 'app' (a native-app-only article, no external link)
// gets its own distinct tint so it reads apart from both at a glance.
function sourceChipStyle(sourceType: string): {
  label: string;
  background: string;
  color: string;
  border: string;
} {
  if (sourceType === 'manual') {
    return {
      label: 'Manual',
      background: 'transparent',
      color: Palette.default.text ?? '',
      border: Palette.default.border ?? '',
    };
  }
  if (sourceType === 'app') {
    return {
      label: 'App Post',
      background: Palette.tone2.background,
      color: Palette.tone2.text ?? '',
      border: Palette.tone2.border ?? '',
    };
  }
  return {
    label: 'Website',
    background: Palette.tone1.activeBg ?? '',
    color: Palette.tone1.activeText ?? '',
    border: Palette.tone1.graphLine ?? '',
  };
}

const GRID_COLUMNS = 'minmax(0, 2fr) 7.5rem 10rem 7rem 7rem 6.5rem';

function formatDate(publishDate: string | null | undefined) {
  if (!publishDate) return '—';
  const date = new Date(publishDate);
  if (Number.isNaN(date.getTime())) return publishDate.slice(0, 10);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const HEADER_LABELS = ['Title', 'Date', 'Publisher', 'Source', 'Status', ''];

export default function NewsAdminList({
  items,
  loading,
  showArchived,
  onToggleArchived,
  onEdit,
  onDelete,
  onUnarchive,
  onPublish,
}: Props) {
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(e) => onToggleArchived(e.target.checked)}
            />
          }
          label={<StyledTypography fontSize="0.875rem">Show archived</StyledTypography>}
        />
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLUMNS,
            columnGap: '1rem',
            px: '0.75rem',
            py: '0.625rem',
            borderBottom: `1px solid ${Palette.tone1.border}`,
          }}
        >
          {HEADER_LABELS.map((label, i) => (
            <StyledTypography
              key={label || `col-${i}`}
              fontSize="0.75rem"
              fontWeight={600}
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                color: Palette.default.disabledText,
                textAlign: i === HEADER_LABELS.length - 1 ? 'right' : 'left',
              }}
            >
              {label}
            </StyledTypography>
          ))}
        </Box>

        {loading && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <StyledTypography>Loading…</StyledTypography>
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <StyledTypography>
              No news items yet. Use “Import from website” or “Add post”.
            </StyledTypography>
          </Box>
        )}

        {!loading &&
          items.map((item) => {
            const status = STATUS_STYLE[item.status] ?? STATUS_STYLE.draft;
            const source = sourceChipStyle(item.sourceType);

            return (
              <Box
                key={item.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  columnGap: '1rem',
                  alignItems: 'center',
                  px: '0.75rem',
                  py: '0.75rem',
                  borderBottom: `1px solid ${Palette.tone1.border}`,
                  '&:hover': { backgroundColor: Palette.tone1.activeBg },
                }}
              >
                <StyledTypography
                  fontWeight={500}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {item.title || '(untitled)'}
                </StyledTypography>
                <StyledTypography fontSize="0.875rem">
                  {formatDate(item.publishDate)}
                </StyledTypography>
                <StyledTypography
                  fontSize="0.875rem"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {item.publisherName ?? '—'}
                </StyledTypography>
                <Chip
                  size="small"
                  label={source.label}
                  sx={{
                    backgroundColor: source.background,
                    color: source.color,
                    border: `1px solid ${source.border}`,
                  }}
                />
                <Chip
                  size="small"
                  label={status.label}
                  sx={{
                    backgroundColor: Palette[status.tone].background,
                    color: Palette[status.tone].text,
                    border: `1px solid ${Palette[status.tone].border}`,
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {item.status === 'draft' && (
                    <Tooltip title="Publish">
                      <IconButton
                        size="small"
                        aria-label="Publish"
                        onClick={() => onPublish(item)}
                        sx={{ color: Palette.active.buttonBg }}
                      >
                        <PublishIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" aria-label="Edit" onClick={() => onEdit(item)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {item.status === 'archived' ? (
                    <Tooltip title="Unarchive">
                      <IconButton
                        size="small"
                        aria-label="Unarchive"
                        onClick={() => onUnarchive(item)}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Archive">
                      <IconButton
                        size="small"
                        aria-label="Archive"
                        onClick={() => onDelete(item)}
                        sx={{ color: Palette.warning.buttonBg }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>
    </>
  );
}
